import React, { useState, useEffect, useContext, useRef } from "react";
import { Container, Row, Col, Form, Label } from "reactstrap";
import {
  IAddUpdateZonalConfigProps,
  IZonalConfigPayload,
  IWarehouseStorageBlock,
  IWarehouseCategory,
  IStorageBlock,
} from "../../../interfaces/Index";
import FormSectionLayout from "../../layouts/FormSectionLayout";
import TextField from "../../common/TextField/TextField";
import ButtonComponent from "../../common/Button/ButtonComponent";
import HeadingText from "../../common/HeadingText/HeadingText";
import Switch from "../../common/Switch/Switch";
import LoaderComponent from "../../common/Loader/Loader";
import { AbilityContext, Can } from "../../../config/Can";
import SearchableSelect from "../../common/SearchableSelect/SearchableSelect";
import {
  normalizeZonalConfigCreatePayload,
  normalizeZonalConfigUpdateReqPayload,
} from "normalizer/ZonalConfigNormalizer";
import { TOWN_TYPE, UC_TYPE, ZONE_TYPE_STATE } from "../../../config/constants";
import LoggingViewer from "components/common/LoggingViewer/LoggingViewer";
import MultiSelectDropDown from "../../common/MultiSelectDropDown/MultiSelectDropDown";
import { Multiselect } from "multiselect-react-dropdown";
import searchicon from "../../../assets/images/search-icon.svg";
import close from "../../../assets/images/close.svg";
import backarrow from "../../../assets/images/back_arrow.svg";

const AddUpdateZonalConfigPage: React.FunctionComponent<
  IAddUpdateZonalConfigProps
> = (props: IAddUpdateZonalConfigProps) => {
  const ability = useContext(AbilityContext);
  const getTypeOption = (typeValue: String) => {
    return ZONE_TYPE_STATE.find((e) => e.label === typeValue);
  };
  const [zonalConfigState, setZonalConfigState] = useState<IZonalConfigPayload>(
    {
      id: props.zonalConfigs ? props.zonalConfigs.id || "" : "",
      type: props.zonalConfigs
        ? getTypeOption(props.zonalConfigs.type) || ""
        : "",
      town: props.zonalConfigs ? props.zonalConfigs.town || "" : "",
      zoneName: props.zonalConfigs ? props.zonalConfigs.zoneName || "" : "",
      zone: props.zonalConfigs ? props.zonalConfigs.zone || "" : "",
      status: props.zonalConfigs
        ? props.zonalConfigs.status === "ACTIVE"
          ? true
          : false
        : false,
      selectedCity: props.selectedCity && props.selectedCity,
      warehouse: "",
      selectedWarehouseListWithStorageBlocks: props.zonalConfigs
        ? props.zonalConfigs.selectedWarehouseListWithStorageBlocks || []
        : [],
      locationPolygonText: props.zonalConfigs
        ? props.zonalConfigs.locationPolygonText || ""
        : "",
      customerChannel: props.zonalConfigs?.customerChannel,
    }
  );

  let validateForm: any = useRef(true);

  let warehouseStorageBlockOptionsMap = useRef<Map<string, IWarehouseCategory>>(
    new Map()
  );

  const [wareHouseState, setWarehouseState] = useState(
    props.warehouseCategories && props.warehouseCategories
  );

  const [zoneState, setZones] = useState(
    (props.zonalConfigChildZones && props.zonalConfigChildZones) || []
  );
  const [townState, setTowns] = useState(
    props.zonalConfigTowns && props.zonalConfigTowns
  );

  const createdAt = props.zonalConfigs?.createdAt
    ? props.zonalConfigs?.createdAt
    : "";
  const createdBy =
    props.bulkCustomerDetails && props.zonalConfigs?.createdBy
      ? props.bulkCustomerDetails[props.zonalConfigs.createdBy]?.fullName
      : "";
  const updatedAt = props.zonalConfigs?.updatedAt
    ? props.zonalConfigs?.updatedAt
    : "";
  const updatedBy =
    props.bulkCustomerDetails && props.zonalConfigs?.updatedBy
      ? props.bulkCustomerDetails[props.zonalConfigs.updatedBy]?.fullName
      : "";

  let selected: IStorageBlock;
  useEffect(() => {
    const { fetchCustomerConfig } = props;
    fetchCustomerConfig();
  }, []);

  useEffect(() => {
    const { isZonalConfigAdded, backButtonHandler, isZonalConfigUpdated } =
      props;
    if (isZonalConfigAdded) {
      backButtonHandler(true, props.selectedCity);
    } else if (isZonalConfigUpdated) {
      backButtonHandler(true, props.selectedCity);
    }
  }, [props.isZonalConfigAdded, props.isZonalConfigUpdated]);

  useEffect(() => {
    const { getWarehouseCategories, getZonalConfigChildZones } = props;
    getWarehouseCategories({ cityId: props.selectedCity.id });
    getZonalConfigChildZones(props.selectedCity.id);
  }, [props.selectedCity.id]);

  useEffect(() => {
    const {
      getBulkCustomerDetailsByIdAction,
      getZonalConfigWarehouseListWithStorageBlocks,
      getZoneCustomerChannelConfig,
    } = props;

    if (isEdit) {
      getZonalConfigWarehouseListWithStorageBlocks(
        zonalConfigState.id,
        zonalConfigState.type.value
      );
      //get user metadata
      const userIds = [];
      if (props.zonalConfigs?.createdBy)
        userIds.push(props.zonalConfigs.createdBy);
      if (props.zonalConfigs?.updatedBy)
        userIds.push(props.zonalConfigs.updatedBy);
      if (userIds.length) getBulkCustomerDetailsByIdAction(userIds);
      if (zonalConfigState.type.value === "UC")
        getZoneCustomerChannelConfig(zonalConfigState.id);
    }
  }, [props.zonalConfigs]);

  useEffect(() => {
    const {
      isZonalConfigWarehouseListWithStorageBlocksFetched,
      zonalConfigWarehouseListWithStorageBlocks,
    } = props;
    if (
      isZonalConfigWarehouseListWithStorageBlocksFetched &&
      zonalConfigWarehouseListWithStorageBlocks
    ) {
      setZonalConfigState({
        ...zonalConfigState,
        selectedWarehouseListWithStorageBlocks:
          zonalConfigWarehouseListWithStorageBlocks,
      });
    }
  }, [
    props.isZonalConfigWarehouseListWithStorageBlocksFetched,
    props.zonalConfigWarehouseListWithStorageBlocks,
  ]);

  useEffect(() => {
    const { warehouseCategories, isWarehouseCategoriesFetched } = props;
    if (isWarehouseCategoriesFetched && warehouseCategories.length > 0) {
      warehouseStorageBlockOptionsMap.current = new Map(
        warehouseCategories.map((it) => [it.id, it])
      );
      setWarehouseState(warehouseCategories);
    }
  }, [
    props.warehouseCategories,
    props.isWarehouseCategoriesFetched,
    props.isZonalConfigWarehouseListWithStorageBlocksFetched,
    props.zonalConfigWarehouseListWithStorageBlocks,
  ]);

  useEffect(() => {
    const { zonalConfigChannelName, isFetchCustomerChannelLoading } = props;

    if (
      !isFetchCustomerChannelLoading &&
      zonalConfigChannelName &&
      zonalConfigState.type.value === "UC"
    ) {
      const customerChannelOption =
        props.customerConfig?.customerChannels?.find(
          (val) => val.id === zonalConfigChannelName
        );
      setZonalConfigState({
        ...zonalConfigState,
        customerChannel: customerChannelOption,
      });
    }
  }, [
    props.isFetchCustomerChannelLoading,
    props.zonalConfigChannelName,
    props.customerConfig?.customerChannels,
  ]);

  useEffect(() => {
    const { isZonalConfigChildZonesFetched, zonalConfigChildZones } = props;
    if (isZonalConfigChildZonesFetched && zonalConfigChildZones) {
      setZones(zonalConfigChildZones);
    }
  }, [props.isZonalConfigChildZonesFetched, props.zonalConfigChildZones]);

  useEffect(() => {
    const { getZonalConfigTowns } = props;
    getZonalConfigTowns(zonalConfigState.zone.id);
  }, [zonalConfigState.zone.id]);

  useEffect(() => {
    const { isZonalConfigTownsFetched, zonalConfigTowns } = props;
    if (isZonalConfigTownsFetched && zonalConfigTowns) {
      setTowns(zonalConfigTowns);
    }
  }, [props.zonalConfigTowns, props.isZonalConfigTownsFetched]);

  useEffect(() => {
    zoneState.filter((e) => {
      if (e.label === zone) {
        setZonalConfigState({
          ...zonalConfigState,
          zone: e,
        });
      }
    });
  }, [zoneState]);

  useEffect(() => {
    if (!townState) {
      return;
    } else {
      townState.filter((e) => {
        if (e.label === town) {
          setZonalConfigState({
            ...zonalConfigState,
            town: e,
          });
        }
      });
    }
  }, [townState]);

  useEffect(() => {
    if (!isEdit) {
      setZonalConfigState({
        ...zonalConfigState,
        town: "",
      });
    }
  }, [zonalConfigState.zone]);

  useEffect(() => {
    if (!status) {
      setZonalConfigState({
        ...zonalConfigState,
        warehouse: false,
      });
    }
  }, [zonalConfigState.status]);

  useEffect(() => {
    if (zonalConfigState.type.value !== UC_TYPE) {
      setZonalConfigState({
        ...zonalConfigState,
        customerChannel: undefined,
      });
    }
  }, [zonalConfigState.type]);

  const {
    status,
    town,
    zoneName,
    selectedCity,
    zone,
    type,
    selectedWarehouseListWithStorageBlocks,
    locationPolygonText,
    customerChannel,
  } = zonalConfigState;
  const {
    isEdit,
    backButtonHandler,
    createZonalConfig,
    updateZonalConfig,
    clearZonalConfigDetails,
    clearBulkCustomerDetailsAction,
    isLoading,
  } = props;

  const buttonText = isEdit ? "Update zone" : "Add a zone";

  const handleGoBackButton = () => {
    isEdit && clearZonalConfigDetails();
    backButtonHandler(false, props.selectedCity);
    clearBulkCustomerDetailsAction();
  };

  const handleSearchableSelect = (e: any, fieldId: string) => {
    setZonalConfigState({
      ...zonalConfigState,
      [fieldId]: e,
    });
  };

  const handleChange = (e: any) => {
    const fieldId: string = e.target.id;
    const fieldValue: string = e.target.value;
    setZonalConfigState({
      ...zonalConfigState,
      [fieldId]: fieldId === "status" ? !zonalConfigState[fieldId] : fieldValue,
    });
  };

  const handleSubmit = (e: any) => {
    if (isEdit) {
      const reqPayload = normalizeZonalConfigUpdateReqPayload(zonalConfigState);
      updateZonalConfig(
        zonalConfigState.id as string,
        zonalConfigState.type.value,
        reqPayload
      );
    } else {
      const reqPayload = normalizeZonalConfigCreatePayload(zonalConfigState);
      createZonalConfig(reqPayload);
    }
  };

  const shouldHaveAtLeastOneStorageBlock = (): boolean => {
    return selectedWarehouseListWithStorageBlocks.some(
      (warehouse) => warehouse.storageBlocks.length >= 1
    );
  };

  const handleWarehouse = (e: IWarehouseStorageBlock[]) => {
    const lastElement = e.pop()!;
    let lastWarehouseStorageBlock: IWarehouseStorageBlock = {
      ...lastElement,
      storageBlocks: [],
    };
    e.push(lastWarehouseStorageBlock);
    setZonalConfigState({
      ...zonalConfigState,
      selectedWarehouseListWithStorageBlocks: e,
    });
  };
  const getStorageBlockOptionsByWarehouseId = (warehouseId: string) => {
    if (warehouseStorageBlockOptionsMap.current.has(warehouseId)) {
      const availableOptions: IStorageBlock[] =
        warehouseStorageBlockOptionsMap.current.get(warehouseId)
          ?.storageBlocks || [];
      const selectedStorageBlocks = selectedWarehouseListWithStorageBlocks
        .map((i: IWarehouseStorageBlock) => i.storageBlocks)
        .flat();

      const filteredOptions = availableOptions.filter(
        (x) => !selectedStorageBlocks.some((e) => e.label === x.label)
      );
      return filteredOptions;
    } else {
      return [];
    }
  };

  const handleWarehouseStorageBlock = (
    warehouseId: string,
    e: IStorageBlock[]
  ) => {
    const index = selectedWarehouseListWithStorageBlocks.findIndex(
      (it: IWarehouseStorageBlock) => it.id === warehouseId
    );
    selectedWarehouseListWithStorageBlocks[index].storageBlocks = e;
    setZonalConfigState({
      ...zonalConfigState,
      selectedWarehouseListWithStorageBlocks:
        zonalConfigState.selectedWarehouseListWithStorageBlocks,
    });
  };

  validateForm.current =
    (isEdit &&
      status &&
      type.value === UC_TYPE &&
      (!zoneName ||
        !town ||
        selectedWarehouseListWithStorageBlocks.length === 0 ||
        !shouldHaveAtLeastOneStorageBlock() ||
        locationPolygonText === "")) ||
    (isEdit && type.value === UC_TYPE && !status && !zoneName) ||
    (isEdit && type.value === TOWN_TYPE && !zoneName)
      ? true
      : false;

  const isValid =
    zone &&
    ((type.value === UC_TYPE && town && zoneName && customerChannel) ||
      (type.value === TOWN_TYPE && zoneName));

  let renderWarehouseStorageBlocksRow = (
    warehouseStorageBlocks: IWarehouseStorageBlock
  ) => {
    return (
      <Container style={{ padding: 0.0 }}>
        <Row style={{ alignItems: "center" }}>
          <Col>{warehouseStorageBlocks.label}</Col>
          <Col xs={10}>
            <Multiselect
              key={`${warehouseStorageBlocks.id}_${Math.random()}`}
              label={"StorageBlocks"}
              options={getStorageBlockOptionsByWarehouseId(
                warehouseStorageBlocks.id
              )}
              displayValue={"label"}
              onSelect={(e: IStorageBlock[]) => {
                handleWarehouseStorageBlock(warehouseStorageBlocks.id, e);
              }}
              onRemove={(e: IStorageBlock[]) => {
                handleWarehouseStorageBlock(warehouseStorageBlocks.id, e);
              }}
              selectedValues={warehouseStorageBlocks.storageBlocks}
              disable={
                !(
                  (ability.can("update", "zone") ||
                    ability.can("create", "zone")) &&
                  (ability.can("upsert", "warehouse") ||
                    ability.can("delete", "warehouse"))
                )
              }
            />
          </Col>
        </Row>
        <hr />
      </Container>
    );
  };
  let renderWarehouseStorageBlocksTable = () => {
    return (
      <>
        <Label className="block text-uppercase label">Storage Blocks</Label>
        <hr />
        {selectedWarehouseListWithStorageBlocks.map(
          (warehouseStorageBlocks: IWarehouseStorageBlock) => {
            return renderWarehouseStorageBlocksRow(warehouseStorageBlocks);
          }
        )}
      </>
    );
  };

  const renderUcConfigForm = () => {
    return (
      <>
        <SearchableSelect
          id="town"
          isSearchable={true}
          options={townState}
          onChange={(e: any) => {
            handleSearchableSelect(e, "town");
          }}
          label="town"
          placeholder="Select town"
          selectedValue={town}
        />
        <TextField
          id="zoneName"
          handleChange={handleChange}
          placeholder="e.g. UC NAME"
          label="UC Name"
          value={zoneName}
        />

        <SearchableSelect
          id="customerChannel"
          isSearchable={true}
          options={
            props.customerConfig ? props.customerConfig.customerChannels : []
          }
          onChange={(e: any) => {
            handleSearchableSelect(e, "customerChannel");
          }}
          label="Customer Channel"
          placeholder="Select customer channel"
          selectedValue={customerChannel}
        />

        <MultiSelectDropDown
          className="warehouse-multi-select"
          label="Warehouse"
          displayValue="label"
          options={wareHouseState}
          onChange={(e: IWarehouseStorageBlock[]) => {
            handleWarehouse(e);
          }}
          onRemove={(e: any) => {
            handleSearchableSelect(e, "selectedWarehouseListWithStorageBlocks");
          }}
          selectedValues={selectedWarehouseListWithStorageBlocks}
          isDisable={
            !(
              (ability.can("update", "zone") ||
                ability.can("create", "zone")) &&
              (ability.can("upsert", "warehouse") ||
                ability.can("delete", "warehouse"))
            )
          }
        />
        {renderWarehouseStorageBlocksTable()}
        {isEdit && (
          <Switch
            id="status"
            name="status"
            label="Status"
            checked={status}
            handleSwitch={handleChange}
            statusVal1="Active"
            statusVal2="Inactive"
          />
        )}
      </>
    );
  };

  const renderTownConfigForm = () => {
    return (
      <>
        <TextField
          id="zoneName"
          handleChange={handleChange}
          placeholder="e.g. TOWN"
          label="TOWN Name"
          value={zoneName}
        />
      </>
    );
  };

  const renderZonalConfigForm = () => {
    return (
      <FormSectionLayout subTitleText="Zone details">
        <TextField
          id="selectedCity"
          label="City"
          placeholder="e.g. Karachi"
          value={selectedCity.name}
          handleChange={handleChange}
          disabled={true}
        />

        <SearchableSelect
          id="type"
          isSearchable={true}
          options={ZONE_TYPE_STATE}
          onChange={(e: any) => {
            handleSearchableSelect(e, "type");
          }}
          label="type"
          placeholder="Select Type"
          selectedValue={type}
          isDisabled={isEdit}
        />

        <SearchableSelect
          id="zone"
          isSearchable={true}
          options={zoneState}
          onChange={(e: any) => {
            handleSearchableSelect(e, "zone");
          }}
          label="zone"
          placeholder="Select zone"
          selectedValue={zone}
          isDisabled={isEdit}
        />
        {type.value === UC_TYPE && renderUcConfigForm()}
        {type.value === TOWN_TYPE && renderTownConfigForm()}
      </FormSectionLayout>
    );
  };

  const renderBoundaries = () => {
    return (
      type.value === UC_TYPE && (
        <FormSectionLayout subTitleText="Boundaries">
          <TextField
            placeholder="Boundaries"
            id="locationPolygonText"
            value={locationPolygonText}
            disabled={isEdit}
          />
        </FormSectionLayout>
      )
    );
  };

  const renderFormButton = () => {
    return (
      <FormSectionLayout subTitleText="">
        <div className="form-row-separator">
          <Row id="addEditButtonRow">
            <Col lg="6">
              {(ability.can("update", "zone") ||
                ability.can("create", "zone")) && (
                <ButtonComponent
                  buttonClass="large-button btn-primary"
                  buttonText={buttonText}
                  onClickHandler={handleSubmit}
                  isDisable={
                    props.isLoading ||
                    validateForm.current ||
                    (!isEdit && !isValid)
                  }
                />
              )}
            </Col>
          </Row>
        </div>
      </FormSectionLayout>
    );
  };

  return (
    <div>
      <Container fluid>
        {isLoading && <LoaderComponent />}
        <Row>
          <Col lg="5">
            <div className="back-button">
              <a href="javascript:void(0)" onClick={handleGoBackButton}>
                {" "}
                {/* TO-DO: Convert this to a button in all compoents. */}
                <span>
                  <img src={backarrow} alt="close" />
                </span>{" "}
                Back to Zones
              </a>
            </div>
            <HeadingText text={buttonText} isMainHeading={true} />
          </Col>

          <Col lg="5">
            {props.zonalConfigs && (
              <LoggingViewer
                createdAt={createdAt}
                createdBy={createdBy}
                updatedAt={updatedAt}
                updatedBy={updatedBy}
              />
            )}
          </Col>
        </Row>
        <Row className="form-container add-update-zonal-config-page">
          <Col xs="12">
            <Form>
              <div className="form-row-separator">
                {renderZonalConfigForm()}
                {isEdit && renderBoundaries()}
                {renderFormButton()}
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddUpdateZonalConfigPage;
